import { useState, useEffect, useContext } from "react"
import { UserContext } from "src/providers"
import axios from "axios"
import { useLogger } from "src/providers/LoggerProvider"

/**
 * Hook to check if the user has any pending documents in knowledge bases
 * where they are GATED_COMMUNITY_ADMIN
 * @returns true if there are pending documents in any admin community
 */
export const usePendingDocuments = (): boolean => {
  const { userCommunities } = useContext(UserContext)
  const [hasPendingDocuments, setHasPendingDocuments] = useState(false)
  const { logger } = useLogger()

  useEffect(() => {
    const checkPendingDocuments = () => {
      if (!userCommunities || userCommunities.length === 0) {
        setHasPendingDocuments(false)
        return
      }

      // Filter communities where user is GATED_COMMUNITY_ADMIN
      const adminCommunities = userCommunities.filter(
        (community) => community.profile === "GATED_COMMUNITY_ADMIN"
      )

      if (adminCommunities.length === 0) {
        setHasPendingDocuments(false)
        return
      }

      // Get all admin community IDs
      const communityIds = adminCommunities.map((community) => community.gatedCommunityId)

      // Build query string with multiple communityId parameters
      const communityIdsParam = communityIds.map((id) => `communityId=${id}`).join("&")

      // Check for pending documents across all admin communities in a single request
      axios
        .get(`/api/km/ingest/documents?${communityIdsParam}&status=PENDING&_limit=1`)
        .then((response) => {
          // Check x-total-count header to see if there are any pending documents
          const totalCount = response.headers["x-total-count"]
          setHasPendingDocuments(totalCount ? parseInt(totalCount, 10) > 0 : false)
        })
        .catch((error) => {
          logger.error("Failed to check pending documents", error)
          setHasPendingDocuments(false)
        })
    }

    checkPendingDocuments()

    // Poll every 15 seconds to keep the status updated
    const intervalId = setInterval(checkPendingDocuments, 15000)

    return () => clearInterval(intervalId)
  }, [userCommunities, logger])

  return hasPendingDocuments
}
